import { Component, computed, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { rxResource } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ModalComponent, ModalButton } from '../../../common-components/modal/modal.component';
import { CardRun, CardRunCandidate, CardsAdminService } from '../../../services/admin/cards-admin.service';

@Component({
  selector: 'app-cards-runs-tab',
  imports: [TranslocoModule, ModalComponent, ReactiveFormsModule, DatePipe],
  templateUrl: './cards-runs-tab.component.html',
  styleUrl: './cards-runs-tab.component.scss'
})
export class CardsRunsTabComponent {
  runs = rxResource({ stream: () => this.cardsAdmin.getRuns$(), defaultValue: [] });
  sortedRuns = computed(() => [...this.runs.value()].sort((a, b) => b.id - a.id));

  showPublishModal = signal(false);
  showVoteModal = signal(false);
  selectedRun = signal<CardRun | null>(null);
  selectedCandidate = signal<CardRunCandidate | null>(null);
  voteDate = new FormControl<string>('', { nonNullable: true });

  publishModalButtons: ModalButton[] = [
    { type: 'secondary', text: this.transloco.translate('cancel'), onClick: () => this.showPublishModal.set(false) },
    { type: 'primary', text: this.transloco.translate('cardsAdminPublish'), onClick: () => this.confirmPublish() }
  ];
  voteModalButtons: ModalButton[] = [
    { type: 'secondary', text: this.transloco.translate('cancel'), onClick: () => this.showVoteModal.set(false) },
    { type: 'primary', text: this.transloco.translate('cardsAdminToVote'), onClick: () => this.confirmToVote() }
  ];

  showDeleteModal = signal(false);
  deleting = signal(false);

  deleteModalButtons: ModalButton[] = [
    { type: 'secondary', text: this.transloco.translate('cancel'), onClick: () => this.closeDelete() },
    { type: 'error', text: this.transloco.translate('delete'), onClick: () => this.confirmDelete() }
  ];

  constructor(
    private cardsAdmin: CardsAdminService,
    private transloco: TranslocoService,
    private snackBar: MatSnackBar
  ) {}

  // Single-flight guard for resume clicks (interceptor snackbars errors).
  private resuming = signal(false);

  // A failed or partial admin run can regenerate ONLY its missing candidates
  // in place; texts/brief come from studio data or the surviving candidates,
  // so one of the two must exist (mirrors the backend's guard). 3 = the
  // backend's STUDIO_CANDIDATES.
  canResume(run: CardRun): boolean {
    return (
      run.kind === 'admin' &&
      (run.status === 'failed' || run.status === 'review') &&
      run.candidates.length < 3 &&
      (run.hasStudio || run.candidates.length > 0)
    );
  }

  resume(run: CardRun): void {
    if (this.resuming()) {
      return;
    }
    this.resuming.set(true);
    this.cardsAdmin.studioResume$(run.id).subscribe({
      next: () => {
        this.resuming.set(false);
        this.snackBar.open(this.transloco.translate('cardsAdminResumed'), '', { duration: 3000 });
        this.runs.reload();
      },
      error: () => {
        this.resuming.set(false);
        this.runs.reload();
      }
    });
  }

  statusClass(run: CardRun): string {
    if (run.status === 'closed' && run.candidates.some((c) => c.isWinner)) {
      return 'color-green';
    }
    if (run.status === 'failed') {
      return 'color-red';
    }
    return '';
  }

  // Mirrors the backend's eligibility rules: review/failed only, nothing published.
  canDelete(run: CardRun): boolean {
    return (run.status === 'review' || run.status === 'failed') && !run.candidates.some((c) => c.isWinner);
  }

  openDelete(run: CardRun): void {
    this.selectedRun.set(run);
    this.showDeleteModal.set(true);
  }

  closeDelete(): void {
    this.showDeleteModal.set(false);
  }

  confirmDelete(): void {
    const run = this.selectedRun();
    if (!run || this.deleting()) {
      return;
    }
    this.deleting.set(true);
    this.cardsAdmin.deleteRun$(run.id).subscribe({
      next: () => {
        this.deleting.set(false);
        this.showDeleteModal.set(false);
        this.snackBar.open(this.transloco.translate('cardsAdminDeleted'), '', { duration: 3000 });
        this.runs.reload();
      },
      error: () => {
        // notDeletable race etc. - interceptor already snackbars; refresh so stale rows vanish.
        this.deleting.set(false);
        this.showDeleteModal.set(false);
        this.runs.reload();
      }
    });
  }

  openPublish(run: CardRun, candidate: CardRunCandidate): void {
    this.selectedRun.set(run);
    this.selectedCandidate.set(candidate);
    this.showPublishModal.set(true);
  }

  openToVote(run: CardRun): void {
    this.selectedRun.set(run);
    this.voteDate.setValue('');
    this.showVoteModal.set(true);
  }

  closePublish(): void {
    this.showPublishModal.set(false);
  }

  closeToVote(): void {
    this.showVoteModal.set(false);
  }

  confirmPublish(): void {
    const run = this.selectedRun();
    const candidate = this.selectedCandidate();
    if (!run || !candidate) {
      return;
    }
    this.cardsAdmin.publishRun$(run.id, candidate.id).subscribe(() => {
      this.showPublishModal.set(false);
      this.snackBar.open(this.transloco.translate('cardsAdminPublished'), '', { duration: 3000 });
      this.runs.reload();
    });
  }

  confirmToVote(): void {
    const run = this.selectedRun();
    if (!run || !this.voteDate.value) {
      return;
    }
    this.cardsAdmin.sendRunToVote$(run.id, this.voteDate.value).subscribe(() => {
      this.showVoteModal.set(false);
      this.snackBar.open(this.transloco.translate('cardsAdminStaged'), '', { duration: 3000 });
      this.runs.reload();
    });
  }
}
